import React, { PureComponent, Fragment } from 'react';
import PropTypes from 'prop-types';
import {
    Paper, TextField, Popper, FormHelperText
} from '@mui/material';
import { withStyles } from '@mui/styles';
import Autosuggest from 'react-autosuggest';
import debounce from 'lodash/debounce';

const DEBOUNCE_TIME = 300;
const MIN_COUNT_OF_LETTERS = 2;

const styles = () => ({
    suggestion: {
        display: 'block'
    },
    suggestionsList: {
        zIndex: 100,
        margin: 0,
        padding: 0,
        listStyleType: 'none'
    }
});
function renderInputComponent(inputProps) {
    const { inputRef = () => {}, ref, labelId, error, t, key, ...other } = inputProps;

    return (
        <Fragment>
            <TextField
                key={key}
                label={t(labelId)}
                fullWidth
                error={!!error}
                variant="standard"
                InputProps={{
                    inputRef: node => {
                        ref(node);
                        inputRef(node);
                    },
                }}
                {...other}
            />
            {error && (
                <FormHelperText error={!!error}>
                    {t(error)}
                </FormHelperText>
            )}
        </Fragment>
    );
}

class Typeahead extends PureComponent {
    static propTypes = {
        autoFocus: PropTypes.bool,
        classes: PropTypes.object.isRequired,
        margin: PropTypes.string,
        labelId: PropTypes.string.isRequired,
        disabled: PropTypes.bool,
        getSuggestionValue: PropTypes.func.isRequired,
        handleSuggestionsFetchRequested: PropTypes.func.isRequired,
        handleSuggestionsClearRequested: PropTypes.func.isRequired,
        suggestions: PropTypes.array,
        required: PropTypes.bool,
        error: PropTypes.string,
        onChangeSuggestion: PropTypes.func,
        value: PropTypes.string,
        renderSuggestion: PropTypes.func.isRequired,
        minCountOfLetters: PropTypes.number,
        multiSection: PropTypes.func,
        renderSectionTitle: PropTypes.func,
        getSectionSuggestions: PropTypes.func,
        onDisableButtons: PropTypes.func,
    };

    static defaultProps = {
        disabled: false,
        margin: 'dense',
        required: false
    };

    state = {
        single: this.props.value || '',
        clicked: false
    };

    constructor(props) {
        super(props);

        this.debounced = debounce(this.props.handleSuggestionsFetchRequested, DEBOUNCE_TIME);
    }

    handleSuggestionsFetchRequested = ({ value }) => {
        this.debounced(value);
    };

    handleChange = name => (event, { newValue, method }) => {
        if (this.props.onResetSuggestion && !newValue) {
            this.props.onResetSuggestion();
        }
        if (this.props.onDisableButtons && !this.props.suggestions.length) {
            this.props.onDisableButtons();
        }
        this.setState({
            [name]: newValue,
            clicked: method === 'click' || method === 'enter'
        });
    };

    handleSuggestionSelected = (event, { suggestion, method }) => {
        this.setState({
            clicked: method === 'click' || method === 'enter'
        });
        this.props.onChangeSuggestion(event, suggestion);
    };

    handleBlur = name => () => {
        if (!this.state.clicked) {
            this.setState({
                [name]: this.state.single,
            });
        }
    };

    shouldRenderSuggestions = value => {
        const minCountOfLetters =
            this.props.minCountOfLetters !== undefined ? this.props.minCountOfLetters : MIN_COUNT_OF_LETTERS;

        return value.trim().length >= minCountOfLetters;
    };

    componentDidUpdate(prevProps) {
        if (prevProps.value !== this.props.value) {
            this.setState({ single: this.props.value });
        }
    }

    render() {
        const { classes, t } = this.props;

        const autosuggestProps = {
            shouldRenderSuggestions: this.shouldRenderSuggestions,
            renderInputComponent,
            suggestions: this.props.suggestions,
            onSuggestionSelected: this.handleSuggestionSelected,
            onSuggestionsFetchRequested: this.handleSuggestionsFetchRequested,
            onSuggestionsClearRequested: this.props.handleSuggestionsClearRequested,
            getSuggestionValue: this.props.getSuggestionValue,
            renderSuggestion: this.props.renderSuggestion,
            multiSection: this.props.multiSection,
            renderSectionTitle: this.props.renderSectionTitle,
            getSectionSuggestions: this.props.getSectionSuggestions
        };

        const inputProps = {
            value: this.state.single,
            onChange: this.handleChange('single'),
            margin: this.props.margin,
            labelId: this.props.labelId,
            disabled: this.props.disabled,
            required: this.props.required,
            error: this.props.error,
            autoFocus: this.props.autoFocus,
            t,
            inputRef: node => {
                this.popperNode = node;
            },
        };

        return (
            <Autosuggest
                {...autosuggestProps}
                inputProps={inputProps}
                theme={{
                    suggestionsList: classes.suggestionsList,
                    suggestion: classes.suggestion,
                }}
                renderSuggestionsContainer={options => {
                    const { key, ...containerProps } = options.containerProps;

                    return (
                        <Popper
                            style={{ zIndex: 1400 }}
                            anchorEl={this.popperNode}
                            open={Boolean(options.children)}
                        >
                            <Paper
                                key={key}
                                square
                                {...containerProps}
                                style={{
                                    width: this.popperNode ? this.popperNode.clientWidth : null,
                                    ...{
                                        maxHeight: 300,
                                        overflowY: 'auto'
                                    }
                                }}
                            >
                                {options.children}
                            </Paper>
                        </Popper>
                    );
                }}
            />
        );
    }
}

export default withStyles(styles)(Typeahead);
